import { useState, useEffect, useRef } from "react";
import styles from "../styles/Tooltip.module.css";

// Cache for track data to avoid repeated API calls
const tooltipCache = new Map();

export default function TrackTooltip({
  trackId,
  origin,
  position,
  visible,
  onClose,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!visible || !trackId) {
      return;
    }

    const fetchData = async () => {
      // Check cache first
      if (tooltipCache.has(trackId)) {
        setData(tooltipCache.get(trackId));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `https://phish.in/api/v2/tracks/${trackId}.json`
        );
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const json = await response.json();
        tooltipCache.set(trackId, json);
        setData(json);
      } catch (err) {
        console.error("Failed to fetch track data:", err);
        setError("Failed to load track info");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [trackId, visible]);

  if (!visible) {
    return null;
  }

  const renderContent = () => {
    if (loading) {
      return <div className={styles.loading}>Loading...</div>;
    }

    if (error) {
      return <div className={styles.error}>{error}</div>;
    }

    if (!data) {
      return <div className={styles.error}>Failed to load track info</div>;
    }

    const {
      title,
      show_date,
      venue_name,
      venue_location,
      likes_count,
      tags = [],
    } = data;

    // Check if there's a jamchart tag
    const jamchartTag = tags.find((tag) => tag.name === "Jamcharts");
    const jamchartNote = jamchartTag ? jamchartTag.notes : null;

    // Check for teases
    const teases = tags.filter((tag) => tag.name === "Tease");

    // Create Phish.in show URL
    const phishinShowUrl = `https://phish.in/${show_date}`;

    return (
      <>
        <div className={styles.tooltipHeader}>
          <div className={styles.trackTitle}>{title}</div>
          {origin && <div className={styles.trackOrigin}>{origin}</div>}
        </div>
        <div className={styles.tooltipBody}>
          <div className={styles.trackShow}>
            <strong>
              <a
                href={phishinShowUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {show_date}
              </a>
            </strong>
            <br />
            {venue_name}, {venue_location}
          </div>
          {jamchartNote && (
            <div className={styles.jamchartNote}>
              <span className={styles.jamchartBadge}>⭐ Jamchart</span>
              <div className={styles.jamchartText}>{jamchartNote}</div>
            </div>
          )}
          {teases.map((tease, idx) => (
            <div key={idx} className={styles.tease}>
              🎵 {tease.notes}
            </div>
          ))}
          <div className={styles.trackStats}>❤️ {likes_count || 0} likes</div>
        </div>
      </>
    );
  };

  const tooltipStyle = {
    top: position.top || "0px",
    left: position.left || "0px",
  };

  return visible ? (
    <div
      ref={tooltipRef}
      className={`${styles.tooltip} ${styles.visible}`}
      style={tooltipStyle}
    >
      <div className={styles.tooltipContent}>{renderContent()}</div>
    </div>
  ) : null;
}
